import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDocumentTabs } from '../context/DocumentTabsContext';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  IconButton,
  Chip,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Divider,
  Radio,
  RadioGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  ArrowBack,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save,
  OpenInNew
} from '@mui/icons-material';
import { quoteService } from '../services/quoteService';
import { addressService, contactService } from '../services/customerService';
import NewPartModal from '../components/NewPartModal';
import LineItemPartAutocomplete from '../components/LineItemPartAutocomplete';
import LineItemServiceAutocomplete from '../components/LineItemServiceAutocomplete';
import QuoteLinePopoutRefAutocomplete from '../components/QuoteLinePopoutRefAutocomplete';
import NewCustomerModal from '../components/NewCustomerModal';
import CustomerContactsModal from '../components/CustomerContactsModal';
import CustomerAddressesModal from '../components/CustomerAddressesModal';
import CurrencyTextField from '../components/CurrencyTextField';
import axios from 'axios';
import { effectiveServiceCode } from '../utils/serviceCodeDisplay';
import {
  computeLineItemTaxAmount,
  computeLineTotalWithPerLineTax,
  handleLineTaxPercentArrowKeyDown,
  lineTaxPatchFromAmountInput,
  lineTaxPatchFromPercentInput,
  lineTaxPercentFieldValue,
  lineTaxPercentInputPropsExtra,
  lineItemTaxDollarsEditFieldSx,
  lineItemTaxPercentEditFieldSx,
  documentFooterTaxFieldSx,
  documentFooterTaxRatePercentFieldSx,
  formatTaxPercentDisplayValue,
  sanitizeTaxPercentInputValue,
  normalizeLineTaxModeUi,
  normalizeTaxBasisUi
} from '../utils/lineItemTax';
import {
  hasProposalBreakdownAmounts,
  resolveProposalTotalAmount,
  sumProposalBreakdownAmounts,
} from '../utils/proposalBreakdown';

const formatMoney = (value) => {
  const n = Number(value);
  if (isNaN(n)) return '$0.00';
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Matches QuoteDetail / OrderDetail line-item tables */
const quoteLineTableSx = {
  borderCollapse: 'collapse',
  border: '1px solid #e0e0e0',
  // Shared header/body column sizing (single source of truth, content-aware).
  '& th:nth-of-type(1), & td:nth-of-type(1)': { width: '3% !important', minWidth: 40, textAlign: 'center' },
  '& th:nth-of-type(2), & td:nth-of-type(2)': { width: '4.5% !important', minWidth: 56, textAlign: 'center' },
  '& th:nth-of-type(3), & td:nth-of-type(3)': { width: '23% !important', minWidth: 260 },
  '& th:nth-of-type(4), & td:nth-of-type(4)': { width: '4.25% !important', minWidth: 58, textAlign: 'center' },
  '& th:nth-of-type(5), & td:nth-of-type(5)': { width: '4.75% !important', minWidth: 62, textAlign: 'center' },
  '& th:nth-of-type(6), & td:nth-of-type(6)': { width: '6.5% !important', minWidth: 88, textAlign: 'right' },
  '& th:nth-of-type(7), & td:nth-of-type(7)': { width: '5.5% !important', minWidth: 88, textAlign: 'right' },
  '& th:nth-of-type(8), & td:nth-of-type(8)': { width: '11% !important', minWidth: 128, textAlign: 'right' },
  '& th:nth-of-type(9), & td:nth-of-type(9)': { width: '6.5% !important', minWidth: 88, textAlign: 'right' },
  '& th:nth-of-type(10), & td:nth-of-type(10)': { width: '6% !important', minWidth: 74 },
  '& th:nth-of-type(11), & td:nth-of-type(11)': { width: '18% !important', minWidth: 220 },
  '& th:nth-of-type(12), & td:nth-of-type(12)': { width: '8% !important', minWidth: 112 },
  '& .MuiTableCell-head': { verticalAlign: 'middle', textAlign: 'center' },
  '& .MuiTableCell-head:nth-of-type(7)': { textAlign: 'right' },
  '& .MuiTextField-root .MuiInputBase-root': { fontSize: '0.8125rem' },
  '& .MuiTextField-root .MuiOutlinedInput-input': {
    fontSize: '0.8125rem',
    lineHeight: 1.35,
    padding: '4px 8px'
  },
  '& td:nth-of-type(4) .MuiOutlinedInput-input, & td:nth-of-type(5) .MuiOutlinedInput-input': {
    textAlign: 'center'
  },
  '& td:nth-of-type(6) .MuiOutlinedInput-input, & td:nth-of-type(7) .MuiOutlinedInput-input, & td:nth-of-type(8) .MuiOutlinedInput-input, & td:nth-of-type(9) .MuiOutlinedInput-input': {
    textAlign: 'right'
  },
  '& .MuiInputBase-input': { py: 0.5 },
  '& .MuiFormControl-root': {
    width: '100%',
    minWidth: 0,
    '& .MuiInputLabel-root': { fontSize: '0.75rem', lineHeight: 1.35 }
  },
  '& td:nth-of-type(7) .MuiFormControl-root': { minWidth: 72 },
  '& td:nth-of-type(8) .MuiFormControl-root': { minWidth: 112 },
  '& .MuiTextField-root': { width: '100%' },
  '& .MuiFormControl-root .MuiOutlinedInput-root .MuiSelect-select': {
    fontSize: '0.8125rem',
    lineHeight: 1.35,
    fontWeight: 400,
    padding: '4px 8px',
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  '& tbody tr.wm-line-totals-footer > td:nth-of-type(1), & tbody tr.wm-line-totals-footer > td:nth-of-type(2)': {
    textAlign: 'right'
  },
  '& tbody tr.wm-line-totals-footer--subtotal > td:nth-of-type(1)': { textAlign: 'left' },
  '& .MuiChip-root': { height: 22, fontSize: '0.7rem' },
  '& .MuiIconButton-root': { p: 0.35 }
};

const formatPartOptionLabel = (part) => {
  if (!part) return '—';
  const num = String(part.part_number ?? '').trim();
  const desc = String(part.part_description ?? part.description ?? '').trim();
  if (!num) return '—';
  return desc ? `${num} — ${desc}` : num;
};
const formatPartDescription = (part) =>
  part ? (String(part.part_description ?? part.description ?? '').trim() || String(part.part_number ?? '').trim() || '') : '';
const formatServiceOptionLabel = (svc) => {
  if (!svc) return 'Service';
  const code = effectiveServiceCode(svc);
  const desc = String(svc.service_description ?? svc.description ?? '').trim();
  if (!code && !desc) return 'Service';
  return code ? `${code}${desc ? ` — ${desc}` : ''}` : desc;
};

function normalizeQuoteTaxModeUi(v) {
  const s = String(v == null || v === '' ? 'none' : v).trim().toLowerCase();
  if (s === 'percent') return 'percent';
  if (s === 'fixed') return 'fixed';
  return 'none';
}

function computeDisplayedQuoteTax(subtotal, mode, percentStr, fixedStr) {
  const sub = Number(subtotal) || 0;
  const m = normalizeQuoteTaxModeUi(mode);
  if (m === 'percent') {
    const t = String(percentStr ?? '').trim();
    if (t === '') return 0;
    const p = parseFloat(t);
    if (!Number.isFinite(p) || p < 0) return 0;
    return Math.round(sub * (p / 100) * 100) / 100;
  }
  if (m === 'fixed') {
    const t = String(fixedStr ?? '').trim();
    if (t === '') return 0;
    const f = parseFloat(t);
    if (!Number.isFinite(f) || f < 0) return 0;
    return Math.round(f * 100) / 100;
  }
  return 0;
}

/** Two cells: Tax % and Tax $ (Quote New table, per-line tax). */
function renderQuoteNewLineTaxCells({
  isPerLineQuoteTax,
  row,
  disabled,
  onLineTaxPatch,
  computeSubtotal
}) {
  const sub = computeSubtotal(row);
  if (!isPerLineQuoteTax) {
    return [
      <TableCell key="lt-ctrl" sx={{ border: '1px solid #e0e0e0', px: 0.5, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle', textAlign: 'center' }}>
        —
      </TableCell>,
      <TableCell key="lt-amt" sx={{ border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle', textAlign: 'right' }}>
        —
      </TableCell>
    ];
  }
  const mode = normalizeLineTaxModeUi(row.line_tax_mode);
  const taxAmt = computeLineItemTaxAmount(sub, row);
  const taxAmtCents = Math.round((Number(taxAmt) || 0) * 100) / 100;
  const amountFieldValue = mode === 'none' && taxAmtCents === 0 ? '' : taxAmtCents;
  return [
    <TableCell
      key="lt-pct"
      sx={{
        border: '1px solid #e0e0e0',
        px: 0.35,
        py: 0.5,
        fontSize: '0.8125rem',
        verticalAlign: 'middle',
        minWidth: 88,
        textAlign: 'right'
      }}
    >
      <TextField
        size="small"
        type="number"
        disabled={disabled}
        aria-label="Line tax percent"
        value={lineTaxPercentFieldValue(row, sub)}
        onChange={(e) => {
          const patch = lineTaxPatchFromPercentInput(sub, e.target.value);
          if (patch) onLineTaxPatch(patch);
        }}
        onKeyDown={(e) => handleLineTaxPercentArrowKeyDown(e, row, sub, onLineTaxPatch)}
        inputProps={{
          min: 0,
          step: 'any',
          style: { textAlign: 'right' },
          ...lineTaxPercentInputPropsExtra()
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" disableTypography sx={{ mr: 0.25 }}>
              <Box component="span" sx={{ fontSize: '0.8125rem', lineHeight: 1 }}>
                %
              </Box>
            </InputAdornment>
          )
        }}
        sx={lineItemTaxPercentEditFieldSx}
      />
    </TableCell>,
    <TableCell
      key="lt-amt"
      sx={{
        border: '1px solid #e0e0e0',
        px: 0.5,
        py: 0.5,
        fontSize: '0.8125rem',
        verticalAlign: 'middle',
        textAlign: 'right',
        minWidth: 128
      }}
    >
      <CurrencyTextField
        size="small"
        disabled={disabled}
        aria-label="Line tax amount"
        value={amountFieldValue}
        onChange={(e) => {
          const patch = lineTaxPatchFromAmountInput(sub, e.target.value);
          if (patch) onLineTaxPatch(patch);
        }}
        minimumFractionDigits={2}
        maximumFractionDigits={2}
        dollarAdornment
        inputProps={{ style: { textAlign: 'right' } }}
        sx={lineItemTaxDollarsEditFieldSx}
      />
    </TableCell>
  ];
}

const QUOTE_NEW_LINE_TABLE_COLS = 12;

function normalizeSowListStyle(v) {
  const s = String(v == null || v === '' ? 'bullets' : v).trim().toLowerCase();
  if (s === 'numbered' || s === 'number') return 'numbered';
  if (s === 'none' || s === 'plain') return 'none';
  return 'bullets';
}

const AUTOSAVE_DEBOUNCE_MS = 900;

function formatAddressBookOneLine(addr) {
  if (!addr) return '';
  const parts = [
    addr.address_line1,
    addr.address_line2,
    [addr.city, addr.state, addr.zip_code].filter(Boolean).join(', ')
  ].filter(Boolean);
  return parts.join(' · ') || `Address #${addr.id}`;
}

const createEmptyLineItem = (itemTypeRaw = 'part') => {
  const itemType = itemTypeRaw === 'service' ? 'service' : 'part';
  return {
    part_id: itemType === 'part' ? '' : '',
    service_id: itemType === 'service' ? '' : '',
    item_type: itemType,
    quantity: 1,
    unit_price: 0,
    unit: itemType === 'service' ? 'HR' : 'EA',
    description: '',
    notes: '',
    status: 'pending',
    line_tax_mode: 'none',
    line_tax_percent: '',
    line_tax_fixed_amount: ''
  };
};

const QuoteNew = () => {
  const location = useLocation();
  const { registerTabTitle } = useDocumentTabs();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [customers, setCustomers] = useState([]);
  // Helper: add days to a date string (yyyy-MM-dd). Returns yyyy-MM-dd or empty string if invalid (avoids "Invalid time value").
  const addDaysToDate = (dateString, days) => {
    if (!dateString || typeof dateString !== 'string') return '';
    const part = String(dateString).trim().split('T')[0];
    if (!/^\d{4}-\d{2}-\d{2}$/.test(part)) return '';
    const [y, m, d] = part.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    if (isNaN(date.getTime())) return '';
    date.setDate(date.getDate() + days);
    return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
  };
  // Helper: add one month (e.g. 03/05/2026 → 04/05/2026). Returns yyyy-MM-dd or empty string if invalid.
  const addOneMonth = (dateString) => {
    if (!dateString || typeof dateString !== 'string') return '';
    const part = String(dateString).trim().split('T')[0];
    if (!/^\d{4}-\d{2}-\d{2}$/.test(part)) return '';
    const [y, m, d] = part.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    if (isNaN(date.getTime())) return '';
    date.setMonth(date.getMonth() + 1);
    return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
  };

  const initialQuoteDate = new Date().toISOString().split('T')[0];
  const initialFollowupDate = addDaysToDate(initialQuoteDate, 7);
  const initialExpiresDate = addOneMonth(initialQuoteDate);
  const [formData, setFormData] = useState({
    customer_id: '',
    quote_date: initialQuoteDate,
    followup_date: initialFollowupDate,
    expires_on: initialExpiresDate,
    status: 'draft',
    description: '',
    po_number: '',
    quote_style: 'standard',
    sow_scope_sections: [''],
    sow_clarifications_sections: [''],
    proposal_total_amount: 0,
    proposal_materials_amount: 0,
    proposal_labor_amount: 0,
    proposal_rentals_amount: 0,
    proposal_show_breakdown: false,
    sow_list_style: 'bullets',
    shipping_customer_address_id: '',
    shipping_manual_line1: '',
    shipping_manual_line2: '',
    shipping_manual_city: '',
    shipping_manual_state: '',
    shipping_manual_zip: '',
    shipping_manual_country: '',
    tax_basis: 'document',
    tax_mode: 'none',
    tax_percent: '',
    tax_fixed_amount: ''
  });
  const [lineItems, setLineItems] = useState([
    createEmptyLineItem('part'),
    createEmptyLineItem('service')
  ]);
  const [followupDateManuallyEdited, setFollowupDateManuallyEdited] = useState(false);
  const [expiresOnManuallyEdited, setExpiresOnManuallyEdited] = useState(false);
  const [nextQuoteDisplay, setNextQuoteDisplay] = useState('');
  const [nextQuoteNumber, setNextQuoteNumber] = useState(null);
  const [newPartModalOpen, setNewPartModalOpen] = useState(false);
  const [newPartModalRowIndex, setNewPartModalRowIndex] = useState(null);
  const [lineItemPopoutOpen, setLineItemPopoutOpen] = useState(false);
  const [lineItemPopoutIndex, setLineItemPopoutIndex] = useState(null);
  const [lineItemPopoutDraft, setLineItemPopoutDraft] = useState(null);
  const [newCustomerModalOpen, setNewCustomerModalOpen] = useState(false);
  const [newContactModalOpen, setNewContactModalOpen] = useState(false);
  const [customerAddressesModalOpen, setCustomerAddressesModalOpen] = useState(false);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [customerContacts, setCustomerContacts] = useState([]);
  const [selectedQuoteContactIds, setSelectedQuoteContactIds] = useState([]);
  const [draftQuoteId, setDraftQuoteId] = useState(null);
  const [autosaveStatus, setAutosaveStatus] = useState('idle');
  const [autosaveError, setAutosaveError] = useState('');
  const autosaveTimeoutRef = useRef(null);
  const lastSavedSnapshotRef = useRef('');
  const isSubmittingRef = useRef(false);

  // Reserve next quote number (260001) when form loads
  useEffect(() => {
    let mounted = true;
    quoteService.getNextNumber()
      .then((res) => {
        if (mounted && res?.success && res?.data) {
          setNextQuoteDisplay(res.data.display_number || `#${res.data.quote_number}`);
          setNextQuoteNumber(res.data.quote_number);
        }
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Update followup_date and expires_on when quote_date changes (if not manually edited)
  useEffect(() => {
    if (!formData.quote_date) return;
    setFormData(prev => {
      const next = { ...prev };
      if (!followupDateManuallyEdited) next.followup_date = addDaysToDate(formData.quote_date, 7);
      if (!expiresOnManuallyEdited) next.expires_on = addOneMonth(formData.quote_date);
      return next;
    });
  }, [formData.quote_date, followupDateManuallyEdited, expiresOnManuallyEdited]);

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    if (formData.customer_id) {
      const loadCustomerDetails = async () => {
        try {
          const [addrRes, contactRes] = await Promise.all([
            addressService.getByCustomerId(formData.customer_id),
            contactService.getByCustomerId(formData.customer_id)
          ]);
          setCustomerAddresses(addrRes?.data ?? []);
          setCustomerContacts(contactRes?.data ?? []);
        } catch (err) {
          console.error('Error loading customer details:', err);
          setCustomerAddresses([]);
          setCustomerContacts([]);
        }
      };
      loadCustomerDetails();
    } else {
      setCustomerAddresses([]);
      setCustomerContacts([]);
      setSelectedQuoteContactIds([]);
    }
    setFormData((prev) => ({
      ...prev,
      shipping_customer_address_id: '',
      shipping_manual_line1: '',
      shipping_manual_line2: '',
      shipping_manual_city: '',
      shipping_manual_state: '',
      shipping_manual_zip: '',
      shipping_manual_country: ''
    }));
  }, [formData.customer_id]);

  const quoteShippingSelectValue = formData.shipping_customer_address_id
    ? String(formData.shipping_customer_address_id)
    : '';

  const handleQuoteShippingSelect = (e) => {
    const v = e.target.value;
    if (v === '') {
      setFormData((prev) => ({
        ...prev,
        shipping_customer_address_id: '',
        shipping_manual_line1: '',
        shipping_manual_line2: '',
        shipping_manual_city: '',
        shipping_manual_state: '',
        shipping_manual_zip: '',
        shipping_manual_country: ''
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        shipping_customer_address_id: v,
        shipping_manual_line1: '',
        shipping_manual_line2: '',
        shipping_manual_city: '',
        shipping_manual_state: '',
        shipping_manual_zip: '',
        shipping_manual_country: ''
      }));
    }
  };

  const quoteNewShippingSelectRenderValue = (selected) => {
    if (String(formData.shipping_manual_line1 || '').trim()) {
      return [
        formData.shipping_manual_line1,
        formData.shipping_manual_line2,
        [formData.shipping_manual_city, formData.shipping_manual_state, formData.shipping_manual_zip].filter(Boolean).join(', ')
      ]
        .filter(Boolean)
        .join(', ');
    }
    if (selected === '' || selected == null) {
      const def =
        customerAddresses.find((a) => (a.address_type || '').toLowerCase() === 'shipping') ||
        customerAddresses.find((a) => a.is_default) ||
        customerAddresses[0];
      if (def) return `${formatAddressBookOneLine(def)} (default)`;
      const c = customers.find((x) => x.id === parseInt(formData.customer_id, 10));
      const parts = [c?.address_line1, c?.address_line2, [c?.city, c?.state, c?.zip_code].filter(Boolean).join(', ')].filter(Boolean);
      return parts.length ? parts.join(', ') : 'No address on customer profile';
    }
    const row = customerAddresses.find((a) => String(a.id) === String(selected));
    return row ? formatAddressBookOneLine(row) : String(selected);
  };

  const loadCustomers = async () => {
    try {
      const response = await axios.get('/api/customers', {
        params: { limit: 100, activeOnly: true }
      });
      setCustomers(response.data.data || []);
    } catch (err) {
      console.error('Error loading customers:', err);
      setError('Failed to load customers: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleInputChange = (field) => (e) => {
    const value =
      field === 'proposal_show_breakdown'
        ? e.target.checked
        : e.target.value;
    if (field === 'customer_id' && value === '__new_customer__') {
      setNewCustomerModalOpen(true);
      return;
    }
    if (field === 'quote_date') {
      setFormData(prev => {
        const newData = { ...prev, [field]: value };
        if (value) {
          if (!followupDateManuallyEdited) newData.followup_date = addDaysToDate(value, 7);
          if (!expiresOnManuallyEdited) newData.expires_on = addOneMonth(value);
        }
        return newData;
      });
    } else if (field === 'followup_date') {
      setFollowupDateManuallyEdited(true);
      setFormData({ ...formData, [field]: value });
    } else if (field === 'expires_on') {
      setExpiresOnManuallyEdited(true);
      setFormData({ ...formData, [field]: value });
    } else if (field === 'tax_percent') {
      setFormData((prev) => ({
        ...prev,
        tax_percent: sanitizeTaxPercentInputValue(value)
      }));
    } else if (
      field === 'proposal_materials_amount' ||
      field === 'proposal_labor_amount' ||
      field === 'proposal_rentals_amount'
    ) {
      setFormData((prev) => {
        const next = { ...prev, [field]: value };
        if (
          !hasProposalBreakdownAmounts(
            next.proposal_materials_amount,
            next.proposal_labor_amount,
            next.proposal_rentals_amount
          )
        ) {
          return next;
        }
        return {
          ...next,
          proposal_total_amount: sumProposalBreakdownAmounts(
            next.proposal_materials_amount,
            next.proposal_labor_amount,
            next.proposal_rentals_amount
          ),
        };
      });
    } else {
      setFormData({
        ...formData,
        [field]: value
      });
    }
  };

  const handleScopeSectionChange = (index, value) => {
    const sections = Array.isArray(formData.field_scope_sections) ? [...formData.field_scope_sections] : [''];
    sections[index] = value;
    setFormData((prev) => ({ ...prev, field_scope_sections: sections }));
  };

  const handleAddScopeSection = () => {
    const sections = Array.isArray(formData.field_scope_sections) ? [...formData.field_scope_sections] : [];
    sections.push('');
    setFormData((prev) => ({ ...prev, field_scope_sections: sections }));
  };

  const handleRemoveScopeSection = (index) => {
    const sections = Array.isArray(formData.field_scope_sections) ? [...formData.field_scope_sections] : [];
    sections.splice(index, 1);
    setFormData((prev) => ({ ...prev, field_scope_sections: sections.length ? sections : [''] }));
  };

  const handleSowScopeChange = (index, value) => {
    const sections = Array.isArray(formData.sow_scope_sections) ? [...formData.sow_scope_sections] : [''];
    sections[index] = value;
    setFormData((prev) => ({ ...prev, sow_scope_sections: sections }));
  };
  const handleAddSowScope = () => {
    const sections = Array.isArray(formData.sow_scope_sections) ? [...formData.sow_scope_sections] : [];
    sections.push('');
    setFormData((prev) => ({ ...prev, sow_scope_sections: sections }));
  };
  const handleRemoveSowScope = (index) => {
    const sections = Array.isArray(formData.sow_scope_sections) ? [...formData.sow_scope_sections] : [];
    sections.splice(index, 1);
    setFormData((prev) => ({ ...prev, sow_scope_sections: sections.length ? sections : [''] }));
  };

  const handleSowClarChange = (index, value) => {
    const sections = Array.isArray(formData.sow_clarifications_sections)
      ? [...formData.sow_clarifications_sections]
      : [''];
    sections[index] = value;
    setFormData((prev) => ({ ...prev, sow_clarifications_sections: sections }));
  };
  const handleAddSowClar = () => {
    const sections = Array.isArray(formData.sow_clarifications_sections)
      ? [...formData.sow_clarifications_sections]
      : [];
    sections.push('');
    setFormData((prev) => ({ ...prev, sow_clarifications_sections: sections }));
  };
  const handleRemoveSowClar = (index) => {
    const sections = Array.isArray(formData.sow_clarifications_sections)
      ? [...formData.sow_clarifications_sections]
      : [];
    sections.splice(index, 1);
    setFormData((prev) => ({
      ...prev,
      sow_clarifications_sections: sections.length ? sections : ['']
    }));
  };

  const handleAddLineItem = (itemTypeRaw) => {
    const nextType = itemTypeRaw === 'service' ? 'service' : 'part';
    setLineItems((prev) => [...prev, createEmptyLineItem(nextType)]);
  };

  const handleLineItemChange = (index, field) => (e) => {
    const value = e.target.value;
    const newLineItems = [...lineItems];
    newLineItems[index] = {
      ...newLineItems[index],
      [field]: value
    };
    
    // Calculate line total
    if (field === 'quantity' || field === 'unit_price') {
      const quantity = field === 'quantity' ? parseFloat(value) || 0 : newLineItems[index].quantity || 0;
      const unitPrice = field === 'unit_price' ? parseFloat(value) || 0 : newLineItems[index].unit_price || 0;
      newLineItems[index].line_total = quantity * unitPrice;
    }
    
    setLineItems(newLineItems);
  };

  const handleQuoteNewLineTaxPatch = (index, patch) => {
    if (!patch) return;
    setLineItems((prev) => {
      const u = [...prev];
      if (!u[index]) return prev;
      u[index] = { ...u[index], ...patch };
      return u;
    });
  };

  /** Same tax scope presets as Quote Detail / Order Detail; local state only until Create Quote. */
  const applyQuoteNewTaxScopePreset = (preset) => {
    if (preset == null) return;
    setFormData((fd) => {
      if (preset === 'line_items') {
        return {
          ...fd,
          tax_basis: 'per_line',
          tax_mode: 'none',
          tax_percent: '',
          tax_fixed_amount: ''
        };
      }
      if (preset === 'no_tax') {
        return {
          ...fd,
          tax_basis: 'document',
          tax_mode: 'none',
          tax_percent: '',
          tax_fixed_amount: ''
        };
      }
      const currentMode = normalizeQuoteTaxModeUi(fd.tax_mode);
      const nextMode = currentMode === 'none' ? 'percent' : currentMode;
      const next = {
        ...fd,
        tax_basis: 'document',
        tax_mode: nextMode
      };
      if (nextMode === 'percent') {
        next.tax_percent =
          fd.tax_percent != null && String(fd.tax_percent).trim() !== '' ? fd.tax_percent : '';
        next.tax_fixed_amount = '';
      } else {
        next.tax_fixed_amount =
          fd.tax_fixed_amount != null && String(fd.tax_fixed_amount).trim() !== ''
            ? fd.tax_fixed_amount
            : '';
        next.tax_percent = '';
      }
      return next;
    });
  };

  const handleItemRefChange = (index, value, catalogRecord = null) => {
    if (value === '__new_part__') {
      setNewPartModalRowIndex(index);
      setNewPartModalOpen(true);
      return;
    }
    const updated = [...lineItems];
    const row = { ...updated[index] };
    if (value === '' || value == null) {
      row.part_id = '';
      row.service_id = '';
      row.part_number = '';
      row.service_code = '';
      updated[index] = row;
      setLineItems(updated);
      return;
    }
    const isServiceRow = (row.item_type || 'part') === 'service';
    if (isServiceRow) {
      const service = catalogRecord || null;
      if (service) {
        updated[index] = {
          ...row,
          item_type: 'service',
          service_id: String(service.id),
          part_id: '',
          part_number: '',
          service_code: effectiveServiceCode(service) || '',
          description: service.service_description || service.description || '',
          unit_price: service.unit_price || 0,
          unit: service.unit || 'HR'
        };
      }
      setLineItems(updated);
      return;
    }
    const part = catalogRecord || null;
    if (part) {
      updated[index] = {
        ...row,
        item_type: 'part',
        part_id: String(part.id),
        service_id: '',
        service_code: '',
        part_number: part.part_number ?? '',
        description: part.part_description || part.description || '',
        unit_price: part.unit_price || 0,
        unit: part.unit || 'EA'
      };
    }
    setLineItems(updated);
  };


  const openLineItemPopout = (lineIndex) => {
    if (!Number.isInteger(lineIndex) || lineIndex < 0) return;
    const line = lineItems[lineIndex];
    if (!line) return;
    setLineItemPopoutIndex(lineIndex);
    setLineItemPopoutDraft({ ...line });
    setLineItemPopoutOpen(true);
  };

  const closeLineItemPopout = () => {
    setLineItemPopoutOpen(false);
    setLineItemPopoutIndex(null);
    setLineItemPopoutDraft(null);
  };

  const saveLineItemPopout = () => {
    if (!Number.isInteger(lineItemPopoutIndex) || lineItemPopoutIndex < 0 || !lineItemPopoutDraft) return;
    setLineItems((prev) => {
      const updated = [...prev];
      if (!updated[lineItemPopoutIndex]) return updated;
      updated[lineItemPopoutIndex] = { ...updated[lineItemPopoutIndex], ...lineItemPopoutDraft };
      return updated;
    });
    closeLineItemPopout();
  };

  const handleLineItemPopoutRefChange = (value, record = null) => {
    const raw = String(value || '');
    const [kind, rawId] = raw.includes(':') ? raw.split(':') : [null, raw];
    const id = String(rawId || '').trim();
    if (!id) return;
    const part = record?.kind === 'part' ? record.data : kind === 'part' ? record?.data : null;
    const service = record?.kind === 'service' ? record.data : kind === 'service' ? record?.data : null;
    if (!part && !service) return;
    setLineItemPopoutDraft((prev) => ({
      ...(prev || {}),
      item_type: part ? 'part' : 'service',
      part_id: part ? id : '',
      service_id: service ? id : '',
      part_number: part ? (part.part_number ?? '') : '',
      service_code: service ? (effectiveServiceCode(service) || '') : '',
      description: part
        ? (part.part_description || part.description || '')
        : (service.service_description || service.description || ''),
      unit_price: part ? (part.unit_price || 0) : (service.unit_price || 0),
      unit: part ? (part.unit || 'EA') : (service.unit || 'HR')
    }));
  };

  const handleNewCustomerCreated = (customer) => {
    if (!customer) return;
    setCustomers((prev) => [...prev, customer]);
    setFormData((prev) => ({ ...prev, customer_id: customer.id }));
    setNewCustomerModalOpen(false);
  };

  const handleNewPartCreated = (part, lineItemFields) => {
    if (!part || newPartModalRowIndex === null) return;
    const updated = [...lineItems];
    const qty = lineItemFields?.quantity != null && Number(lineItemFields.quantity) > 0 ? Number(lineItemFields.quantity) : 1;
    updated[newPartModalRowIndex] = {
      ...updated[newPartModalRowIndex],
      item_type: 'part',
      part_id: part.id,
      service_id: '',
      service_code: '',
      part_number: part.part_number ?? '',
      description: lineItemFields?.description ?? part.part_description ?? part.description ?? '',
      quantity: qty,
      unit_price: part.unit_price || 0,
      unit: part.unit || 'EA'
    };
    setLineItems(updated);
    setNewPartModalOpen(false);
    setNewPartModalRowIndex(null);
  };

  const handleRemoveLineItem = (index) => {
    setLineItems((prev) => {
      const next = prev.filter((_, i) => i !== index);
      const hasPart = next.some((item) => (item.item_type || 'part') !== 'service');
      const hasService = next.some((item) => (item.item_type || 'part') === 'service');
      if (!hasPart) next.unshift(createEmptyLineItem('part'));
      if (!hasService) next.push(createEmptyLineItem('service'));
      return next;
    });
  };

  const normalizeLineItemsForApi = (items) =>
    items.map((item) => {
      const itemType = item.item_type === 'service' ? 'service' : 'part';
      const qtyRaw = parseFloat(item.quantity) || 0;
      const quantity =
        itemType === 'service' ? Math.round(qtyRaw * 100) / 100 : Math.max(0, Math.trunc(Math.round(qtyRaw)));
      const ltMode = normalizeLineTaxModeUi(item.line_tax_mode);
      let line_tax_percent = null;
      let line_tax_fixed_amount = null;
      if (ltMode === 'percent') {
        const t = String(item.line_tax_percent ?? '').trim();
        if (t !== '') {
          const p = parseFloat(t);
          if (Number.isFinite(p) && p >= 0) line_tax_percent = p;
        }
      } else if (ltMode === 'fixed') {
        const t = String(item.line_tax_fixed_amount ?? '').trim();
        if (t !== '') {
          const f = parseFloat(t);
          if (Number.isFinite(f) && f >= 0) line_tax_fixed_amount = Math.round(f * 100) / 100;
        }
      }
      return {
        item_type: itemType,
        part_id: itemType === 'part' ? (item.part_id ? parseInt(item.part_id, 10) : null) : null,
        service_id: itemType === 'service' ? (item.service_id ? parseInt(item.service_id, 10) : null) : null,
        quantity,
        unit_price: parseFloat(item.unit_price) || 0,
        description: item.description || '',
        notes: item.notes || '',
        status: item.status || 'pending',
        unit: item.unit || (itemType === 'service' ? 'HR' : 'EA'),
        line_tax_mode: ltMode,
        line_tax_percent,
        line_tax_fixed_amount
      };
    });

  /** Rows with no part, no service, and no description are left in the UI but omitted from save/validation. */
  const getSubstantiveLineItems = (items) =>
    items.filter((item) => {
      const hasPart = item.part_id != null && String(item.part_id).trim() !== '';
      const hasSvc = item.service_id != null && String(item.service_id).trim() !== '';
      const hasDesc = item.description && String(item.description).trim() !== '';
      return hasPart || hasSvc || hasDesc;
    });

  const getValidDraftLineItems = (items) =>
    normalizeLineItemsForApi(getSubstantiveLineItems(items)).filter(
      (item) =>
        (item.part_id || item.service_id || (item.description && String(item.description).trim())) &&
        item.quantity > 0
    );

  const withStandardTaxFields = (payload) => {
    if (payload.quote_style !== 'standard') return payload;
    const basis = normalizeTaxBasisUi(formData.tax_basis);
    const docMode = normalizeQuoteTaxModeUi(formData.tax_mode);
    let tax_percent = null;
    let tax_fixed_amount = null;
    if (basis !== 'per_line') {
      if (docMode === 'percent') {
        const t = String(formData.tax_percent ?? '').trim();
        if (t !== '') {
          const p = parseFloat(t);
          if (Number.isFinite(p) && p >= 0) tax_percent = Math.round(p * 100) / 100;
        }
      } else if (docMode === 'fixed') {
        const t = String(formData.tax_fixed_amount ?? '').trim();
        if (t !== '') {
          const f = parseFloat(t);
          if (Number.isFinite(f) && f >= 0) tax_fixed_amount = Math.round(f * 100) / 100;
        }
      }
    }
    return {
      ...payload,
      tax_basis: basis,
      tax_mode: basis === 'per_line' ? 'none' : docMode,
      tax_percent: basis === 'per_line' ? null : tax_percent,
      tax_fixed_amount: basis === 'per_line' ? null : tax_fixed_amount
    };
  };

  const getBaseQuotePayload = () =>
    withStandardTaxFields({
      ...formData,
      customer_id: parseInt(formData.customer_id, 10),
      quote_style: formData.quote_style || 'standard',
      field_scope_sections: [],
      field_materials_amount: 0,
      field_labor_amount: 0,
      field_rentals_amount: 0,
      field_show_breakdown: false,
      sow_scope_sections: (formData.sow_scope_sections || []).map((s) => String(s || '').trim()).filter(Boolean),
      sow_clarifications_sections: (formData.sow_clarifications_sections || [])
        .map((s) => String(s || '').trim())
        .filter(Boolean),
      proposal_materials_amount: parseFloat(formData.proposal_materials_amount) || 0,
      proposal_labor_amount: parseFloat(formData.proposal_labor_amount) || 0,
      proposal_rentals_amount: parseFloat(formData.proposal_rentals_amount) || 0,
      proposal_total_amount: resolveProposalTotalAmount({
        proposal_total_amount: formData.proposal_total_amount,
        proposal_materials_amount: formData.proposal_materials_amount,
        proposal_labor_amount: formData.proposal_labor_amount,
        proposal_rentals_amount: formData.proposal_rentals_amount,
      }),
      proposal_show_breakdown: !!formData.proposal_show_breakdown,
      sow_list_style: normalizeSowListStyle(formData.sow_list_style),
      followup_date: formData.followup_date || null,
      expires_on: formData.expires_on || null,
      valid_until: formData.expires_on || null,
      po_number:
        formData.po_number != null && String(formData.po_number).trim() !== ''
          ? String(formData.po_number).trim()
          : null,
      contact_ids: selectedQuoteContactIds.filter(id => id != null && id !== ''),
      shipping_customer_address_id: formData.shipping_customer_address_id
        ? parseInt(formData.shipping_customer_address_id, 10)
        : null,
      shipping_manual_line1: formData.shipping_manual_line1,
      shipping_manual_line2: formData.shipping_manual_line2,
      shipping_manual_city: formData.shipping_manual_city,
      shipping_manual_state: formData.shipping_manual_state,
      shipping_manual_zip: formData.shipping_manual_zip,
      shipping_manual_country: formData.shipping_manual_country
    });
  const partLineItemsWithIndex = lineItems
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => (item.item_type || 'part') !== 'service');
  const serviceLineItemsWithIndex = lineItems
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => (item.item_type || 'part') === 'service');
  const partLineCount = partLineItemsWithIndex.length;

  useEffect(() => {
    const customerId = parseInt(formData.customer_id, 10);
    const hasDraftMinimum = Number.isInteger(customerId) && customerId > 0 && String(formData.description || '').trim();
    if (!hasDraftMinimum || isSubmittingRef.current) {
      return;
    }

    const snapshot = JSON.stringify({
      customer_id: customerId,
      quote_date: formData.quote_date || null,
      followup_date: formData.followup_date || null,
      expires_on: formData.expires_on || null,
      description: String(formData.description || '').trim(),
      po_number: String(formData.po_number || '').trim(),
      quote_style: formData.quote_style || 'standard',
      sow_scope_sections: (formData.sow_scope_sections || []).map((s) => String(s || '').trim()).filter(Boolean),
      sow_clarifications_sections: (formData.sow_clarifications_sections || [])
        .map((s) => String(s || '').trim())
        .filter(Boolean),
      proposal_total_amount: parseFloat(formData.proposal_total_amount) || 0,
      proposal_materials_amount: parseFloat(formData.proposal_materials_amount) || 0,
      proposal_labor_amount: parseFloat(formData.proposal_labor_amount) || 0,
      proposal_rentals_amount: parseFloat(formData.proposal_rentals_amount) || 0,
      proposal_show_breakdown: !!formData.proposal_show_breakdown,
      sow_list_style: normalizeSowListStyle(formData.sow_list_style),
      contact_ids: selectedQuoteContactIds.filter(id => id != null && id !== '').map((id) => parseInt(id, 10)).sort((a, b) => a - b),
      lineItems: formData.quote_style === 'standard' ? getValidDraftLineItems(lineItems) : [],
      shipping_customer_address_id: formData.shipping_customer_address_id || null,
      shipping_manual_line1: formData.shipping_manual_line1 || '',
      shipping_manual_line2: formData.shipping_manual_line2 || '',
      shipping_manual_city: formData.shipping_manual_city || '',
      shipping_manual_state: formData.shipping_manual_state || '',
      shipping_manual_zip: formData.shipping_manual_zip || '',
      shipping_manual_country: formData.shipping_manual_country || '',
      tax_basis: normalizeTaxBasisUi(formData.tax_basis),
      tax_mode: normalizeQuoteTaxModeUi(formData.tax_mode),
      tax_percent: String(formData.tax_percent ?? '').trim(),
      tax_fixed_amount: String(formData.tax_fixed_amount ?? '').trim()
    });

    if (snapshot === lastSavedSnapshotRef.current) {
      return;
    }

    if (autosaveTimeoutRef.current) clearTimeout(autosaveTimeoutRef.current);
    autosaveTimeoutRef.current = setTimeout(async () => {
      try {
        setAutosaveStatus('saving');
        setAutosaveError('');
        const payload = {
          ...getBaseQuotePayload(),
          status: 'draft'
        };
        const validDraftItems = formData.quote_style === 'standard' ? getValidDraftLineItems(lineItems) : [];

        if (!draftQuoteId) {
          const createPayload = { ...payload, lineItems: validDraftItems };
          if (nextQuoteNumber != null) createPayload.quote_number = nextQuoteNumber;
          const response = await quoteService.create(createPayload);
          if (response?.success && response?.data?.id) {
            const createdId = response.data.id;
            const created = response.data;
            setDraftQuoteId(createdId);
            setNextQuoteNumber(null);
            const label =
              created.identifier ||
              (created.quote_number != null ? String(created.quote_number) : null);
            if (label) {
              registerTabTitle(location.pathname, `Quote ${label}`);
            }
            // Stay on /quotes/new so autosave does not swap to QuoteDetail (read-only until Edit).
          }
        } else {
          await quoteService.update(draftQuoteId, payload);
          if (formData.quote_style === 'standard') {
            await quoteService.replaceDraftLineItems(draftQuoteId, validDraftItems);
          }
        }

        lastSavedSnapshotRef.current = snapshot;
        setAutosaveStatus('saved');
      } catch (err) {
        setAutosaveStatus('error');
        setAutosaveError(err.response?.data?.message || err.message || 'Failed to autosave draft');
      }
    }, AUTOSAVE_DEBOUNCE_MS);

    return () => {
      if (autosaveTimeoutRef.current) clearTimeout(autosaveTimeoutRef.current);
    };
  }, [formData, selectedQuoteContactIds, lineItems, draftQuoteId, nextQuoteNumber, location.pathname, registerTabTitle]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
      autosaveTimeoutRef.current = null;
    }
    
    if (!formData.customer_id) {
      setError('Please select a customer');
      return;
    }

    const customerId = parseInt(formData.customer_id);
    if (isNaN(customerId)) {
      setError('Invalid customer selected. Please try selecting the customer again.');
      return;
    }

    const substantiveLineItems =
      formData.quote_style === 'standard' ? getSubstantiveLineItems(lineItems) : [];

    if (formData.quote_style === 'standard' && substantiveLineItems.length === 0) {
      setError('Please add at least one line item to the quote.');
      return;
    }

    // Validate quote description is provided
    if (!formData.description || !String(formData.description).trim()) {
      setError('Description is required.');
      return;
    }

    if (formData.quote_style === 'standard') {
      const invalidLineItems = substantiveLineItems.filter(
        (item) =>
          (!item.part_id && !item.service_id && !item.description) ||
          !item.quantity ||
          parseFloat(item.quantity) <= 0
      );

      if (invalidLineItems.length > 0) {
        setError('Please ensure all line items have a part or description, quantity greater than 0, and unit price.');
        return;
      }
    } else if (formData.quote_style === 'scope_of_work') {
      const scopeCount = (formData.sow_scope_sections || [])
        .map((s) => String(s || '').trim())
        .filter(Boolean).length;
      if (scopeCount === 0) {
        setError('Please add at least one scope section for the proposal.');
        return;
      }
      const total = resolveProposalTotalAmount({
        proposal_total_amount: formData.proposal_total_amount,
        proposal_materials_amount: formData.proposal_materials_amount,
        proposal_labor_amount: formData.proposal_labor_amount,
        proposal_rentals_amount: formData.proposal_rentals_amount,
      });
      if (total <= 0) {
        setError('Please enter a proposal total amount greater than zero.');
        return;
      }
    }

    try {
      isSubmittingRef.current = true;
      setSaving(true);
      setError('');

      const quoteData = {
        ...getBaseQuotePayload(),
        customer_id: customerId,
        status: 'open',
        lineItems:
          formData.quote_style === 'standard' ? normalizeLineItemsForApi(substantiveLineItems) : []
      };

      if (nextQuoteNumber != null) {
        quoteData.quote_number = nextQuoteNumber;
      }

      let response;
      if (draftQuoteId) {
        await quoteService.update(draftQuoteId, { ...quoteData, lineItems: undefined });
        if (formData.quote_style === 'standard') {
          await quoteService.replaceDraftLineItems(draftQuoteId, quoteData.lineItems);
        }
        response = { success: true, data: { id: draftQuoteId } };
      } else {
        response = await quoteService.create(quoteData);
      }
      
      if (response.success) {
        navigate(`/quotes/${response.data.id}`);
      } else {
        setError(response.message || 'Failed to create quote');
      }
    } catch (err) {
      console.error('Error creating quote:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to create quote';
      setError(errorMessage);
    } finally {
      setSaving(false);
      isSubmittingRef.current = false;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      open: 'info',
      won: 'success',
      lost: 'error',
      expired: 'warning'
    };
    return colors[status] || 'default';
  };

  const quoteNewTaxBasisUi = normalizeTaxBasisUi(formData.tax_basis);
  const isPerLineQuoteNewTax = quoteNewTaxBasisUi === 'per_line';
  const quoteNewLineItemsTaxToggleActive = lineItems.length > 0;
  const quoteNewTaxScopeToggleValue = isPerLineQuoteNewTax
    ? 'line_items'
    : normalizeQuoteTaxModeUi(formData.tax_mode) === 'none'
      ? 'no_tax'
      : 'order';
  const computeQuoteNewLineSubtotal = (row) => {
    const q = Number(row?.quantity) || 0;
    const p = Number(row?.unit_price) || 0;
    return Math.round(q * p * 100) / 100;
  };
  const quoteNewLineSubtotalColumnAmount = (row) => {
    const sub = computeQuoteNewLineSubtotal(row);
    return isPerLineQuoteNewTax ? computeLineTotalWithPerLineTax(sub, row) : sub;
  };
  let quoteNewLineItemsSubtotal = 0;
  let quoteNewLineItemsTaxSum = 0;
  for (const item of lineItems) {
    const sub = computeQuoteNewLineSubtotal(item);
    quoteNewLineItemsSubtotal += sub;
    quoteNewLineItemsTaxSum += computeLineItemTaxAmount(sub, item);
  }
  const displayQuoteNewTax = isPerLineQuoteNewTax
    ? Math.round(quoteNewLineItemsTaxSum * 100) / 100
    : computeDisplayedQuoteTax(
        quoteNewLineItemsSubtotal,
        formData.tax_mode,
        formData.tax_percent,
        formData.tax_fixed_amount
      );
  const displayQuoteNewGrand = Math.round((quoteNewLineItemsSubtotal + displayQuoteNewTax) * 100) / 100;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/quotes')} size="small" sx={{ mb: 0.5 }}>
            Back
          </Button>
          <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
            Create New Quote
          </Typography>
          {(autosaveStatus !== 'idle' || draftQuoteId) && (
            <Typography variant="caption" color={autosaveStatus === 'error' ? 'error' : 'text.secondary'}>
              {autosaveStatus === 'saving' && 'Saving draft...'}
              {autosaveStatus === 'saved' && `Draft saved${draftQuoteId ? ` (#${draftQuoteId})` : ''}`}
              {autosaveStatus === 'error' && `Draft autosave failed${autosaveError ? `: ${autosaveError}` : ''}`}
              {autosaveStatus !== 'saving' && autosaveStatus !== 'saved' && autosaveStatus !== 'error' && draftQuoteId ? `Draft saved (#${draftQuoteId})` : ''}
            </Typography>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        {/* General Information */}
        <Card sx={{ mb: 1.5 }}>
          <CardContent sx={{ p: 1.5 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 1 }}>General Information</Typography>
            {nextQuoteDisplay && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Quote identifier will be generated after you save.
              </Typography>
            )}
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={[{ id: '__new_customer__', __isNew: true }, ...customers]}
                  getOptionLabel={(opt) => {
                    if (opt?.__isNew) return 'New Customer';
                    return opt ? (opt.company_name || `${opt.first_name || ''} ${opt.last_name || ''}`.trim() || opt.customer_number || '') : '';
                  }}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  value={
                    !formData.customer_id
                      ? null
                      : customers.find((c) => c.id === parseInt(formData.customer_id, 10)) || null
                  }
                  onChange={(e, newValue) => {
                    if (!newValue) {
                      setFormData((prev) => ({ ...prev, customer_id: '' }));
                      return;
                    }
                    if (newValue.__isNew) {
                      setNewCustomerModalOpen(true);
                      return;
                    }
                    setFormData((prev) => ({ ...prev, customer_id: newValue.id }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Customer" required size="small" margin="dense" />
                  )}
                  size="small"
                  sx={{ mb: 0.5 }}
                />
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={handleInputChange('description')}
                  margin="dense"
                  size="small"
                  multiline
                  minRows={4}
                  maxRows={4}
                  required
                  sx={{
                    '& .MuiInputBase-inputMultiline': {
                      overflowY: 'auto',
                      resize: 'none',
                    },
                  }}
                />
                <TextField
                  fullWidth
                  label="Customer PO (optional)"
                  value={formData.po_number}
                  onChange={handleInputChange('po_number')}
                  margin="dense"
                  size="small"
                  placeholder="Customer's purchase order reference"
                />
                <FormControl fullWidth margin="dense" size="small">
                  <InputLabel>Quote Style</InputLabel>
                  <Select
                    value={formData.quote_style}
                    onChange={handleInputChange('quote_style')}
                    label="Quote Style"
                  >
                    <MenuItem value="standard">Standard</MenuItem>
                    <MenuItem value="scope_of_work">Field Work / Service</MenuItem>
                  </Select>
                </FormControl>
                {formData.customer_id ? (
                  <>
                    <FormControl fullWidth margin="dense" size="small" variant="outlined">
                      <InputLabel id="wm-quote-new-ship-label" shrink>
                        Quote shipping address
                      </InputLabel>
                      <Select
                        labelId="wm-quote-new-ship-label"
                        id="wm-quote-new-ship-select"
                        notched
                        label="Quote shipping address"
                        displayEmpty
                        value={quoteShippingSelectValue}
                        renderValue={quoteNewShippingSelectRenderValue}
                        onChange={handleQuoteShippingSelect}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 420, maxWidth: 560 } } }}
                      >
                        <MenuItem value="" sx={{ py: 0.75, alignItems: 'flex-start' }}>
                          <Typography
                            variant="caption"
                            sx={{ fontSize: '0.75rem', lineHeight: 1.35, whiteSpace: 'normal', display: 'block' }}
                          >
                            {(() => {
                              const def =
                                customerAddresses.find((a) => (a.address_type || '').toLowerCase() === 'shipping') ||
                                customerAddresses.find((a) => a.is_default) ||
                                customerAddresses[0];
                              if (def) return `Default — ${formatAddressBookOneLine(def)}`;
                              const c = customers.find((x) => x.id === parseInt(formData.customer_id, 10));
                              const parts = [c?.address_line1, [c?.city, c?.state, c?.zip_code].filter(Boolean).join(', ')].filter(Boolean);
                              const detail = parts.length ? parts.join(', ') : 'No address on customer profile';
                              return `Default — ${detail}`;
                            })()}
                          </Typography>
                        </MenuItem>
                        {customerAddresses.map((addr) => {
                          const t = (addr.address_type || '').toLowerCase();
                          const tag =
                            t === 'shipping' ? 'Shipping' : t === 'billing' ? 'Billing' : addr.is_default ? 'Default' : 'Address';
                          return (
                            <MenuItem key={addr.id} value={String(addr.id)} sx={{ py: 0.75, alignItems: 'flex-start' }}>
                              <Typography
                                variant="caption"
                                sx={{ fontSize: '0.75rem', lineHeight: 1.35, whiteSpace: 'normal', display: 'block' }}
                              >
                                {`${tag}: ${formatAddressBookOneLine(addr)}`}
                              </Typography>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Add saved addresses with <strong>Add address</strong> under Customer Information below.
                    </Typography>
                  </>
                ) : null}
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Quote Date"
                  type="date"
                  value={formData.quote_date}
                  onChange={handleInputChange('quote_date')}
                  margin="dense"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiInputBase-input': { minWidth: 180 } }}
                  required
                />
                <TextField
                  fullWidth
                  label="Followup Date"
                  type="date"
                  value={formData.followup_date}
                  onChange={handleInputChange('followup_date')}
                  margin="dense"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiInputBase-input': { minWidth: 180 } }}
                />
                <TextField
                  fullWidth
                  label="Expires On"
                  type="date"
                  value={formData.expires_on}
                  onChange={handleInputChange('expires_on')}
                  margin="dense"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiInputBase-input': { minWidth: 180 } }}
                />
                <FormControl fullWidth margin="dense" size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={handleInputChange('status')}
                    label="Status"
                    disabled
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="open">Open</MenuItem>
                  </Select>
                </FormControl>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={formData.status}
                    color={getStatusColor(formData.status)}
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Box>
              </Grid>
            </Grid>
            {formData.customer_id && (
              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Customer Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="caption" color="textSecondary" display="block">Address</Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setCustomerAddressesModalOpen(true)}
                      >
                        Add address
                      </Button>
                    </Box>
                    {customerAddresses.length > 0 ? (
                      customerAddresses.filter(a => a.is_default || a.address_type === 'billing').slice(0, 1).map((addr) => (
                        <Typography key={addr.id} variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {addr.address_line1}
                          {addr.address_line2 && `\n${addr.address_line2}`}
                          {addr.city && `\n${addr.city}${addr.state ? `, ${addr.state}` : ''} ${addr.zip_code || ''}`}
                        </Typography>
                      ))
                    ) : (
                      (() => {
                        const c = customers.find(x => x.id === parseInt(formData.customer_id));
                        if (!c) return <Typography variant="body2" color="textSecondary">—</Typography>;
                        return (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {c.address_line1 || '—'}
                            {c.address_line2 && `\n${c.address_line2}`}
                            {c.city && `\n${c.city}${c.state ? `, ${c.state}` : ''} ${c.zip_code || ''}`}
                          </Typography>
                        );
                      })()
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="caption" color="textSecondary" display="block">Contacts for this quote</Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setNewContactModalOpen(true)}
                      >
                        Add contact
                      </Button>
                    </Box>
                    <Autocomplete
                      multiple
                      size="small"
                      options={customerContacts.filter(c => c.is_active !== false)}
                      getOptionLabel={(opt) => opt.name + (opt.title ? ` (${opt.title})` : '') + (opt.email_address ? ` — ${opt.email_address}` : '')}
                      isOptionEqualToValue={(opt, val) => opt.id === val.id}
                      value={customerContacts.filter(c => selectedQuoteContactIds.includes(c.id))}
                      onChange={(e, newValue) => setSelectedQuoteContactIds(newValue.map(c => c.id))}
                      noOptionsText={customerContacts.length === 0 ? 'No contacts. Click "Add contact" to create one for this customer.' : 'No options'}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select contacts" />
                      )}
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1, mb: 0.5 }}>Quote contacts</Typography>
                    {selectedQuoteContactIds.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">No contacts selected for this quote</Typography>
                    ) : (
                      customerContacts
                        .filter(c => selectedQuoteContactIds.includes(c.id))
                        .map((contact) => (
                          <Typography key={contact.id} variant="body2" sx={{ mb: 0.5 }}>
                            {contact.name}
                            {contact.phone_number && ` — ${contact.phone_number}`}
                            {contact.email_address && ` (${contact.email_address})`}
                          </Typography>
                        ))
                    )}
                  </Grid>
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>

        {formData.quote_style === 'scope_of_work' && (
          <Card sx={{ mb: 1.5 }}>
            <CardContent sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 1 }}>
                Field Work / Service
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                Describe scope and clarifications in sections. Enter the total proposal amount; optionally show a
                Materials / Labor / Rentals breakdown.
              </Typography>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                Scope of work
              </Typography>
              {(formData.sow_scope_sections || []).map((section, index) => (
                <Box key={`sow-scope-${index}`} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label={index === 0 ? 'Scope' : `Scope ${index + 1}`}
                    value={section}
                    onChange={(e) => handleSowScopeChange(index, e.target.value)}
                    size="small"
                  />
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveSowScope(index)}
                    disabled={(formData.sow_scope_sections || []).length <= 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button size="small" variant="outlined" onClick={handleAddSowScope} sx={{ mb: 2 }}>
                Add scope section
              </Button>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Clarifications & exclusions
              </Typography>
              {(formData.sow_clarifications_sections || []).map((section, index) => (
                <Box key={`sow-clar-${index}`} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label={index === 0 ? 'Clarification' : `Clarification ${index + 1}`}
                    value={section}
                    onChange={(e) => handleSowClarChange(index, e.target.value)}
                    size="small"
                  />
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveSowClar(index)}
                    disabled={(formData.sow_clarifications_sections || []).length <= 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button size="small" variant="outlined" onClick={handleAddSowClar} sx={{ mb: 1.5 }}>
                Add clarification section
              </Button>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <CurrencyTextField
                    fullWidth
                    size="small"
                    label="Proposal total amount"
                    dollarAdornment
                    minimumFractionDigits={2}
                    maximumFractionDigits={2}
                    value={formData.proposal_total_amount}
                    onChange={handleInputChange('proposal_total_amount')}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CurrencyTextField
                    fullWidth
                    size="small"
                    label="Materials"
                    dollarAdornment
                    minimumFractionDigits={2}
                    maximumFractionDigits={2}
                    value={formData.proposal_materials_amount}
                    onChange={handleInputChange('proposal_materials_amount')}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CurrencyTextField
                    fullWidth
                    size="small"
                    label="Labor"
                    dollarAdornment
                    minimumFractionDigits={2}
                    maximumFractionDigits={2}
                    value={formData.proposal_labor_amount}
                    onChange={handleInputChange('proposal_labor_amount')}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CurrencyTextField
                    fullWidth
                    size="small"
                    label="Rentals"
                    dollarAdornment
                    minimumFractionDigits={2}
                    maximumFractionDigits={2}
                    value={formData.proposal_rentals_amount}
                    onChange={handleInputChange('proposal_rentals_amount')}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
                  Printed Version Options:
                </Typography>
                <Box
                  sx={{
                    width: '66.666%',
                    maxWidth: '100%',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'stretch',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: { xs: 'none', sm: '1 1 0' },
                      width: { xs: '100%', sm: 'auto' },
                      minWidth: 0,
                      boxSizing: 'border-box',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      px: 1.5,
                      py: 1,
                      bgcolor: 'action.hover',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        fontWeight: 600,
                        color: 'text.secondary',
                        letterSpacing: 0.2,
                        textTransform: 'uppercase',
                        mb: 0.75,
                      }}
                    >
                      Price breakdown
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                      Materials, labor, and rentals lines on the printed quote
                    </Typography>
                    <FormControlLabel
                      sx={{
                        m: 0,
                        alignItems: 'center',
                      }}
                      control={
                        <Checkbox
                          checked={!!formData.proposal_show_breakdown}
                          onChange={handleInputChange('proposal_show_breakdown')}
                        />
                      }
                      label="Show price breakdown on Print Version"
                    />
                  </Box>
                  <Divider
                    flexItem
                    orientation="vertical"
                    sx={{
                      display: { xs: 'none', sm: 'block' },
                    }}
                  />
                  <Box
                    sx={{
                      flex: { xs: 'none', sm: '1 1 0' },
                      width: { xs: '100%', sm: 'auto' },
                      minWidth: 0,
                      boxSizing: 'border-box',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      px: 1.5,
                      py: 1,
                      bgcolor: 'action.hover',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      id="quote-new-sow-list-caption"
                      variant="caption"
                      sx={{
                        display: 'block',
                        fontWeight: 600,
                        color: 'text.secondary',
                        letterSpacing: 0.2,
                        textTransform: 'uppercase',
                        mb: 0.75,
                      }}
                    >
                      List format (choose one)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                      How scope and clarifications appear on the printed quote
                    </Typography>
                    <RadioGroup
                      row
                      aria-labelledby="quote-new-sow-list-caption"
                      name="sow_list_style"
                      sx={{ flexWrap: 'wrap', gap: 0.5 }}
                      value={normalizeSowListStyle(formData.sow_list_style)}
                      onChange={handleInputChange('sow_list_style')}
                    >
                      <FormControlLabel
                        value="numbered"
                        sx={{ mr: 1.5 }}
                        control={<Radio size="small" />}
                        label="Numbered items"
                      />
                      <FormControlLabel
                        value="bullets"
                        sx={{ mr: 1.5 }}
                        control={<Radio size="small" />}
                        label="Bullet points"
                      />
                      <FormControlLabel value="none" control={<Radio size="small" />} label="None" />
                    </RadioGroup>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Line Items — layout aligned with Quote Detail / Order Detail */}
        {formData.quote_style === 'standard' && (
        <Card sx={{ mb: 1.5 }}>
          <CardContent sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                color="success"
                size="small"
                onClick={() => handleAddLineItem('part')}
              >
                + Add Part
              </Button>
            </Box>

            {lineItems.length === 0 ? (
              <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                No line items. Click &quot;+ Add Part&quot; or &quot;+ Add Service&quot; to add items to this quote.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small" sx={quoteLineTableSx}>
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={QUOTE_NEW_LINE_TABLE_COLS} sx={{ border: '1px solid #e0e0e0', bgcolor: '#50a8ee', fontWeight: 700 }}>
                        Parts and Materials
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="center" sx={{ width: 18, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.5, py: 0.5, fontSize: '0.75rem', bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden', fontWeight: 600 }}>Item</TableCell>
                      <TableCell align="center" sx={{ width: 60, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden', fontWeight: 600 }}>Code</TableCell>
                      <TableCell align="center" sx={{ width: 120, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden', fontWeight: 600 }}>Part</TableCell>
                      <TableCell align="center" sx={{ width: 40, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden', fontWeight: 600 }}>Qty</TableCell>
                      <TableCell align="center" sx={{ width: 44, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden', fontWeight: 600 }}>Unit</TableCell>
                      <TableCell align="center" sx={{ width: 70, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden', fontWeight: 600 }}>Price</TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          width: 88,
                          minWidth: 88,
                          whiteSpace: 'nowrap',
                          border: '1px solid #ccc',
                          px: 0.35,
                          py: 0.5,
                          fontSize: '0.7rem',
                          bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden',
                          fontWeight: 600
                        }}
                      >
                        Tax %
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          width: 128,
                          minWidth: 128,
                          whiteSpace: 'nowrap',
                          border: '1px solid #ccc',
                          px: 0.75,
                          py: 0.5,
                          fontSize: '0.75rem',
                          bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden',
                          fontWeight: 600,
                          textAlign: 'right'
                        }}
                      >
                        Tax $
                      </TableCell>
                      <TableCell align="center" sx={{ width: 64, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden', fontWeight: 600 }}>
                        {isPerLineQuoteNewTax ? 'Line total' : 'Subtotal'}
                      </TableCell>
                      <TableCell align="center" sx={{ width: 88, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden', fontWeight: 600 }}>Status</TableCell>
                      <TableCell align="center" sx={{ width: 220, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden', fontWeight: 600 }}>Notes</TableCell>
                      <TableCell align="center" sx={{ width: 64, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden', fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                    {partLineItemsWithIndex.map(({ item, index }, pIdx) => {
                      const partNumber = item.part_number != null && String(item.part_number).trim() !== ''
                        ? String(item.part_number).trim()
                        : '—';
                      return (
                        <TableRow key={`part-${index}`}>
                          <TableCell sx={{ width: 18, border: '1px solid #e0e0e0', px: 0.5, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle', textAlign: 'center' }}>
                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.5, py: 0.25, minHeight: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8125rem' }}>{pIdx + 1}</Box>
                          </TableCell>
                          <TableCell sx={{ width: 60, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle', textAlign: 'center' }}>
                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.5, py: 0.25, minHeight: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8125rem' }}>{partNumber}</Box>
                          </TableCell>
                          <TableCell sx={{ width: 120, maxWidth: 120, minWidth: 0, overflow: 'hidden', border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            <LineItemPartAutocomplete
                              valueId={item.part_id}
                              onCommit={(v, part) => handleItemRefChange(index, v, part)}
                              label="Part"
                              noMargin
                              textFieldSx={{
                                '& .MuiInputBase-root': { fontSize: '0.8125rem' }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ width: 40, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle', textAlign: 'center' }}>
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantity}
                              onChange={handleLineItemChange(index, 'quantity')}
                              inputProps={{ min: 0, step: 1, style: { textAlign: 'center' } }}
                            />
                          </TableCell>
                          <TableCell sx={{ width: 44, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            <TextField
                              size="small"
                              value={item.unit || (item.item_type === 'service' ? 'HR' : 'EA')}
                              onChange={handleLineItemChange(index, 'unit')}
                            />
                          </TableCell>
                          <TableCell sx={{ width: 70, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle', textAlign: 'right' }}>
                            <TextField
                              type="number"
                              size="small"
                              value={item.unit_price != null ? item.unit_price : ''}
                              onChange={handleLineItemChange(index, 'unit_price')}
                              inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                            />
                          </TableCell>
                          {renderQuoteNewLineTaxCells({
                            isPerLineQuoteTax: isPerLineQuoteNewTax,
                            row: item,
                            disabled: saving,
                            onLineTaxPatch: (patch) => handleQuoteNewLineTaxPatch(index, patch),
                            computeSubtotal: computeQuoteNewLineSubtotal
                          })}
                          <TableCell sx={{ width: 78, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle', textAlign: 'right' }}>
                            {formatMoney(quoteNewLineSubtotalColumnAmount(item))}
                          </TableCell>
                          <TableCell sx={{ width: 74, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            <FormControl fullWidth size="small">
                              <Select
                                value={item.status || 'pending'}
                                onChange={handleLineItemChange(index, 'status')}
                              >
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="won">Won</MenuItem>
                                <MenuItem value="lost">Lost</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell sx={{ width: 220, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', width: '100%' }}>
                              <TextField
                                size="small"
                                value={item.notes || ''}
                                onChange={handleLineItemChange(index, 'notes')}
                                sx={{ flex: 1, minWidth: 0 }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ width: 64, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            <IconButton size="small" color="primary" onClick={() => openLineItemPopout(index)} title="Pop out full line item" aria-label="Pop out full line item">
                              <OpenInNew fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleRemoveLineItem(index)} title="Remove">
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow>
                      <TableCell colSpan={QUOTE_NEW_LINE_TABLE_COLS} sx={{ border: 0, height: 14, bgcolor: '#ffffff', p: 0 }} />
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={QUOTE_NEW_LINE_TABLE_COLS} sx={{ border: 0, p: 0.5, bgcolor: 'transparent' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            startIcon={<AddIcon />}
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => handleAddLineItem('service')}
                          >
                            + Add Service
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={QUOTE_NEW_LINE_TABLE_COLS} sx={{ border: '1px solid #e0e0e0', borderTop: '8px solid #ffffff', bgcolor: '#50a8ee', fontWeight: 700 }}>
                        Labor and Services
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="center" sx={{ width: 18, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.5, py: 0.5, fontSize: '0.75rem', bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden', fontWeight: 600 }}>Item</TableCell>
                      <TableCell align="center" sx={{ width: 60, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden', fontWeight: 600 }}>Code</TableCell>
                      <TableCell align="center" sx={{ width: 120, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden', fontWeight: 600 }}>Service</TableCell>
                      <TableCell align="center" sx={{ width: 40, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden', fontWeight: 600 }}>Qty</TableCell>
                      <TableCell align="center" sx={{ width: 44, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden', fontWeight: 600 }}>Unit</TableCell>
                      <TableCell align="center" sx={{ width: 70, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden', fontWeight: 600 }}>Price</TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          width: 88,
                          minWidth: 88,
                          whiteSpace: 'nowrap',
                          border: '1px solid #ccc',
                          px: 0.35,
                          py: 0.5,
                          fontSize: '0.7rem',
                          bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden',
                          fontWeight: 600
                        }}
                      >
                        Tax %
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          width: 128,
                          minWidth: 128,
                          whiteSpace: 'nowrap',
                          border: '1px solid #ccc',
                          px: 0.75,
                          py: 0.5,
                          fontSize: '0.75rem',
                          bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden',
                          fontWeight: 600,
                          textAlign: 'right'
                        }}
                      >
                        Tax $
                      </TableCell>
                      <TableCell align="center" sx={{ width: 64, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden', fontWeight: 600 }}>
                        {isPerLineQuoteNewTax ? 'Line total' : 'Subtotal'}
                      </TableCell>
                      <TableCell align="center" sx={{ width: 88, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden', fontWeight: 600 }}>Status</TableCell>
                      <TableCell align="center" sx={{ width: 220, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden', fontWeight: 600 }}>Notes</TableCell>
                      <TableCell align="center" sx={{ width: 64, whiteSpace: 'nowrap', border: '1px solid #ccc', px: 0.75, py: 0.5, fontSize: '0.75rem', bgcolor: '#aed9f7', position: 'relative', zIndex: 2, overflow: 'hidden', fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                    {serviceLineItemsWithIndex.map(({ item, index }, sIdx) => {
                      const code = item.service_code != null && String(item.service_code).trim() !== ''
                        ? String(item.service_code).trim()
                        : '—';
                      return (
                        <TableRow key={`svc-${index}`}>
                          <TableCell sx={{ width: 18, border: '1px solid #e0e0e0', px: 0.5, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle', textAlign: 'center' }}>
                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.5, py: 0.25, minHeight: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8125rem' }}>{partLineCount + sIdx + 1}</Box>
                          </TableCell>
                          <TableCell sx={{ width: 60, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle', textAlign: 'center' }}>
                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.5, py: 0.25, minHeight: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8125rem' }}>{code}</Box>
                          </TableCell>
                          <TableCell sx={{ width: 120, maxWidth: 120, minWidth: 0, overflow: 'hidden', border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            <LineItemServiceAutocomplete
                              valueId={item.service_id}
                              onCommit={(v, service) => handleItemRefChange(index, v, service)}
                              label="Service"
                              noMargin
                              textFieldSx={{
                                '& .MuiInputBase-root': { fontSize: '0.8125rem' }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ width: 40, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle', textAlign: 'center' }}>
                            <TextField type="number" size="small" value={item.quantity} onChange={handleLineItemChange(index, 'quantity')} inputProps={{ min: 0, step: 1, style: { textAlign: 'center' } }} />
                          </TableCell>
                          <TableCell sx={{ width: 44, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            <TextField size="small" value={item.unit || 'HR'} onChange={handleLineItemChange(index, 'unit')} />
                          </TableCell>
                          <TableCell sx={{ width: 70, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle', textAlign: 'right' }}>
                            <TextField
                              type="number"
                              size="small"
                              value={item.unit_price != null ? item.unit_price : ''}
                              onChange={handleLineItemChange(index, 'unit_price')}
                              inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                            />
                          </TableCell>
                          {renderQuoteNewLineTaxCells({
                            isPerLineQuoteTax: isPerLineQuoteNewTax,
                            row: item,
                            disabled: saving,
                            onLineTaxPatch: (patch) => handleQuoteNewLineTaxPatch(index, patch),
                            computeSubtotal: computeQuoteNewLineSubtotal
                          })}
                          <TableCell sx={{ width: 78, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle', textAlign: 'right' }}>
                            {formatMoney(quoteNewLineSubtotalColumnAmount(item))}
                          </TableCell>
                          <TableCell sx={{ width: 74, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            <FormControl fullWidth size="small">
                              <Select value={item.status || 'pending'} onChange={handleLineItemChange(index, 'status')}>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="won">Won</MenuItem>
                                <MenuItem value="lost">Lost</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell sx={{ width: 220, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', width: '100%' }}>
                              <TextField size="small" value={item.notes || ''} onChange={handleLineItemChange(index, 'notes')} sx={{ flex: 1, minWidth: 0 }} />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ width: 64, border: '1px solid #e0e0e0', px: 0.75, py: 0.5, fontSize: '0.8125rem', verticalAlign: 'middle' }}>
                            <IconButton size="small" color="primary" onClick={() => openLineItemPopout(index)} title="Pop out full line item" aria-label="Pop out full line item">
                              <OpenInNew fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleRemoveLineItem(index)} title="Remove">
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {lineItems.length > 0 && (
                      <>
                        <TableRow className="wm-line-totals-footer wm-line-totals-footer--subtotal" sx={{ bgcolor: '#eceff1' }}>
                          <TableCell colSpan={8} align="left" sx={{ border: '1px solid #e0e0e0', verticalAlign: 'middle' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                              {quoteNewLineItemsTaxToggleActive && (
                                <ToggleButtonGroup
                                  exclusive
                                  size="small"
                                  value={quoteNewTaxScopeToggleValue}
                                  disabled={saving}
                                  onChange={(_, v) => {
                                    if (v != null) applyQuoteNewTaxScopePreset(v);
                                  }}
                                  aria-label="Quote tax scope"
                                  sx={{
                                    flexShrink: 0,
                                    '& .MuiToggleButton-root': {
                                      px: 0.75,
                                      py: 0.35,
                                      fontSize: '0.6875rem',
                                      textTransform: 'none',
                                      lineHeight: 1.25
                                    }
                                  }}
                                >
                                  <ToggleButton value="line_items">Tax line items</ToggleButton>
                                  <ToggleButton value="order">Tax order</ToggleButton>
                                  <ToggleButton value="no_tax">No tax</ToggleButton>
                                </ToggleButtonGroup>
                              )}
                              <Typography
                                component="span"
                                sx={{ flex: 1, textAlign: 'right', fontWeight: 700, fontSize: '0.875rem', minWidth: 0 }}
                              >
                                Line items total
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ border: '1px solid #e0e0e0', fontWeight: 700, fontSize: '0.875rem', textAlign: 'right' }}>
                            {formatMoney(quoteNewLineItemsSubtotal)}
                          </TableCell>
                          <TableCell colSpan={3} sx={{ border: '1px solid #e0e0e0' }} />
                        </TableRow>
                        <TableRow className="wm-line-totals-footer" sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell colSpan={8} align="right" sx={{ border: '1px solid #e0e0e0', fontWeight: 600, fontSize: '0.875rem', verticalAlign: 'middle', textAlign: 'right', py: 0.5 }}>
                            {isPerLineQuoteNewTax ? 'Line Items Tax Total' : 'Tax Total'}
                          </TableCell>
                          <TableCell align="right" sx={{ border: '1px solid #e0e0e0', fontWeight: 600, fontSize: '0.875rem', verticalAlign: 'middle', textAlign: 'right', py: 0.5 }}>
                            {formatMoney(displayQuoteNewTax)}
                          </TableCell>
                          <TableCell colSpan={3} sx={{ border: '1px solid #e0e0e0', verticalAlign: 'middle', py: 0.5 }}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-start', alignItems: 'center', py: 0 }}>
                              {formData.tax_mode === 'percent' && (
                                <TextField
                                  margin="none"
                                  size="small"
                                  aria-label="Tax rate percent"
                                  sx={documentFooterTaxRatePercentFieldSx}
                                  value={formData.tax_percent}
                                  disabled={saving || isPerLineQuoteNewTax}
                                  onChange={handleInputChange('tax_percent')}
                                  onBlur={() => {
                                    setFormData((fd) => ({
                                      ...fd,
                                      tax_percent:
                                        String(fd.tax_percent ?? '').trim() === ''
                                          ? ''
                                          : formatTaxPercentDisplayValue(fd.tax_percent)
                                    }));
                                  }}
                                  inputProps={{ min: 0, step: 0.01, inputMode: 'decimal' }}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end" disableTypography sx={{ ml: 0, mr: 0 }}>
                                        <Box component="span" sx={{ fontSize: '0.8125rem', lineHeight: 1, userSelect: 'none' }}>
                                          %
                                        </Box>
                                      </InputAdornment>
                                    )
                                  }}
                                />
                              )}
                              {formData.tax_mode === 'fixed' && (
                                <TextField
                                  margin="none"
                                  size="small"
                                  label="Amount"
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ width: 104, ...documentFooterTaxFieldSx }}
                                  value={formData.tax_fixed_amount}
                                  disabled={saving || isPerLineQuoteNewTax}
                                  onChange={handleInputChange('tax_fixed_amount')}
                                  inputProps={{ min: 0, step: 0.01 }}
                                />
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow className="wm-line-totals-footer" sx={{ bgcolor: '#eceff1' }}>
                          <TableCell colSpan={8} align="right" sx={{ border: '1px solid #e0e0e0', fontWeight: 700, fontSize: '0.875rem', textAlign: 'right' }}>
                            Total (incl. tax)
                          </TableCell>
                          <TableCell align="right" sx={{ border: '1px solid #e0e0e0', fontWeight: 700, fontSize: '0.875rem', textAlign: 'right' }}>
                            {formatMoney(displayQuoteNewGrand)}
                          </TableCell>
                          <TableCell colSpan={3} sx={{ border: '1px solid #e0e0e0' }} />
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate('/quotes')}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="small"
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
            disabled={saving}
          >
            {saving
              ? draftQuoteId
                ? 'Updating...'
                : 'Creating...'
              : draftQuoteId
                ? 'Update Quote'
                : 'Create Quote'}
          </Button>
        </Box>
      </form>

      <NewPartModal
        open={newPartModalOpen}
        onClose={() => {
          setNewPartModalOpen(false);
          setNewPartModalRowIndex(null);
        }}
        onCreated={handleNewPartCreated}
      />

      <NewCustomerModal
        open={newCustomerModalOpen}
        onClose={() => setNewCustomerModalOpen(false)}
        onCreated={handleNewCustomerCreated}
      />

      <Dialog
        open={lineItemPopoutOpen}
        onClose={closeLineItemPopout}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Line Item</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1.5, display: 'grid', gap: 1.25 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '80% 20%' }, width: '100%' }}>
              <QuoteLinePopoutRefAutocomplete
                draft={lineItemPopoutDraft}
                onSelectValue={handleLineItemPopoutRefChange}
              />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center' }}>
              <TextField
                size="small"
                label="Type"
                value={(lineItemPopoutDraft?.item_type || 'part') === 'service' ? 'Service' : 'Part'}
                InputProps={{ readOnly: true }}
                sx={{ width: 120 }}
              />
              <TextField
                size="small"
                label="Code"
                value={
                  (lineItemPopoutDraft?.item_type || 'part') === 'service'
                    ? (lineItemPopoutDraft?.service_code || '—')
                    : (lineItemPopoutDraft?.part_number || '—')
                }
                InputProps={{ readOnly: true }}
                sx={{ width: 140 }}
              />
              <TextField
                size="small"
                label="Status"
                value={lineItemPopoutDraft?.status || 'pending'}
                InputProps={{ readOnly: true }}
                sx={{ width: 120 }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
              <TextField
                size="small"
                type="number"
                label="Quantity"
                value={lineItemPopoutDraft?.quantity ?? ''}
                onChange={(e) => setLineItemPopoutDraft((prev) => ({ ...(prev || {}), quantity: e.target.value }))}
                inputProps={{ min: 0, step: 1, style: { textAlign: 'center' } }}
                sx={{ width: 120 }}
              />
              <TextField
                size="small"
                label="Unit"
                value={lineItemPopoutDraft?.unit || ((lineItemPopoutDraft?.item_type || 'part') === 'service' ? 'HR' : 'EA')}
                onChange={(e) => setLineItemPopoutDraft((prev) => ({ ...(prev || {}), unit: e.target.value }))}
                sx={{ width: 120 }}
              />
              <TextField
                size="small"
                type="number"
                label="Unit Price"
                value={lineItemPopoutDraft?.unit_price ?? ''}
                onChange={(e) => setLineItemPopoutDraft((prev) => ({ ...(prev || {}), unit_price: e.target.value }))}
                inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                sx={{ width: 140 }}
              />
              <TextField
                size="small"
                label="Subtotal"
                value={formatMoney((Number(lineItemPopoutDraft?.quantity) || 0) * (Number(lineItemPopoutDraft?.unit_price) || 0))}
                InputProps={{ readOnly: true }}
                sx={{ width: 140 }}
              />
            </Box>

            {isPerLineQuoteNewTax && lineItemPopoutDraft && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'flex-start' }}>
                <TextField
                  size="small"
                  type="number"
                  aria-label="Line tax percent"
                  value={lineTaxPercentFieldValue(
                    lineItemPopoutDraft,
                    computeQuoteNewLineSubtotal(lineItemPopoutDraft)
                  )}
                  onChange={(e) => {
                    const sub = computeQuoteNewLineSubtotal(lineItemPopoutDraft);
                    const patch = lineTaxPatchFromPercentInput(sub, e.target.value);
                    if (patch) setLineItemPopoutDraft((prev) => ({ ...(prev || {}), ...patch }));
                  }}
                  onKeyDown={(e) => {
                    const sub = computeQuoteNewLineSubtotal(lineItemPopoutDraft);
                    handleLineTaxPercentArrowKeyDown(e, lineItemPopoutDraft, sub, (patch) =>
                      setLineItemPopoutDraft((prev) => ({ ...(prev || {}), ...patch }))
                    );
                  }}
                  inputProps={{
                    min: 0,
                    step: 'any',
                    style: { textAlign: 'right' },
                    ...lineTaxPercentInputPropsExtra()
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" disableTypography sx={{ mr: 0.25 }}>
                        <Box component="span" sx={{ fontSize: '0.8125rem', lineHeight: 1 }}>
                          %
                        </Box>
                      </InputAdornment>
                    )
                  }}
                  sx={{ width: 88, maxWidth: 96, ...lineItemTaxPercentEditFieldSx }}
                />
                <CurrencyTextField
                  size="small"
                  aria-label="Line tax amount"
                  value={(() => {
                    const sub = computeQuoteNewLineSubtotal(lineItemPopoutDraft);
                    const mode = normalizeLineTaxModeUi(lineItemPopoutDraft.line_tax_mode);
                    const amt = Math.round((Number(computeLineItemTaxAmount(sub, lineItemPopoutDraft)) || 0) * 100) / 100;
                    return mode === 'none' && amt === 0 ? '' : amt;
                  })()}
                  onChange={(e) => {
                    const sub = computeQuoteNewLineSubtotal(lineItemPopoutDraft);
                    const patch = lineTaxPatchFromAmountInput(sub, e.target.value);
                    if (patch) setLineItemPopoutDraft((prev) => ({ ...(prev || {}), ...patch }));
                  }}
                  minimumFractionDigits={2}
                  maximumFractionDigits={2}
                  dollarAdornment
                  inputProps={{ style: { textAlign: 'right' } }}
                  sx={{ width: 200, minWidth: 180, ...lineItemTaxDollarsEditFieldSx }}
                />
              </Box>
            )}

            <TextField
              size="small"
              label="Description"
              multiline
              rows={4}
              value={lineItemPopoutDraft?.description || ''}
              onChange={(e) => setLineItemPopoutDraft((prev) => ({ ...(prev || {}), description: e.target.value }))}
              sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}
            />
            <TextField
              size="small"
              label="Notes"
              multiline
              rows={3}
              value={lineItemPopoutDraft?.notes || ''}
              onChange={(e) => setLineItemPopoutDraft((prev) => ({ ...(prev || {}), notes: e.target.value }))}
              sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLineItemPopout}>Cancel</Button>
          <Button variant="contained" onClick={saveLineItemPopout} color="primary" disabled={!lineItemPopoutDraft}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <CustomerContactsModal
        open={newContactModalOpen && !!formData.customer_id}
        onClose={async () => {
          setNewContactModalOpen(false);
          if (formData.customer_id) {
            try {
              const res = await contactService.getByCustomerId(formData.customer_id);
              setCustomerContacts(res?.data ?? []);
            } catch (err) {
              console.error('Error reloading contacts:', err);
            }
          }
        }}
        customerId={formData.customer_id ? parseInt(formData.customer_id) : null}
        customerName={
          (() => {
            const c = customers.find((x) => x.id === parseInt(formData.customer_id));
            return c?.company_name || `${c?.first_name || ''} ${c?.last_name || ''}`.trim() || 'Customer';
          })()
        }
      />

      <CustomerAddressesModal
        open={customerAddressesModalOpen && !!formData.customer_id}
        onClose={async () => {
          setCustomerAddressesModalOpen(false);
          if (formData.customer_id) {
            try {
              const res = await addressService.getByCustomerId(formData.customer_id);
              setCustomerAddresses(res?.data ?? []);
            } catch (err) {
              console.error('Error reloading addresses:', err);
            }
          }
        }}
        customerId={formData.customer_id ? parseInt(formData.customer_id) : null}
        customerName={
          (() => {
            const c = customers.find((x) => x.id === parseInt(formData.customer_id));
            return c?.company_name || `${c?.first_name || ''} ${c?.last_name || ''}`.trim() || 'Customer';
          })()
        }
      />
    </Box>
  );
};

export default QuoteNew;
